import type { FamilyOverviewChild } from '../../api/models';
import { AppCard } from '../../components/AppCard';
import { AppText } from '../../components/AppText';
import { RetryNotice } from '../../components/RetryNotice';
import { SecondaryButton } from '../../components/SecondaryButton';
import { Strings } from '../../strings';

import { isEmpty } from './OverviewState';
import { useOverview } from './useOverview';

/**
 * The family at a glance.
 * Mirrors android/…/Screens/Overview/OverviewScreen.kt.
 *
 * „Prehľad" was a bottom-navigation destination that the screen inventory never defined, so all three
 * clients pointed it at the children list and the two tabs showed the same thing (R0087). This is the
 * screen it should have had.
 *
 * It shows facts side by side and draws no conclusion from them. Two children's numbers appearing on one
 * screen is not a comparison; ranking them, or calling one number better than another, would be — and
 * BR-13 forbids it however it is phrased.
 */
export function OverviewScreen({ onOpenDashboard }: { onOpenDashboard: (childId: string) => void }) {
  const model = useOverview();
  const overview = model.state.overview;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
      <AppText as="h1" style="titleL">
        {Strings.navOverview}
      </AppText>

      {model.state.error !== null ? (
        <RetryNotice
          text={model.state.error}
          correlationId={model.state.errorCorrelationId}
          tone="error"
          onRetry={() => void model.load()}
        />
      ) : null}

      {overview !== null ? (
        <>
          <AppCard>
            <AppText style="titleS">{Strings.overviewAccountTitle}</AppText>
            <AppText color="var(--text-secondary)" sx={{ marginTop: 'var(--space-1)' }}>
              {overview.account.email}
            </AppText>
            <AppText color="var(--text-secondary)" sx={{ marginTop: 'var(--space-1)' }}>
              {overview.account.emailVerified
                ? Strings.overviewEmailVerified
                : Strings.overviewEmailUnverified}
            </AppText>
          </AppCard>

          {isEmpty(model.state) ? (
            <AppCard>
              <AppText style="titleS">{Strings.overviewChildrenTitle}</AppText>
              <AppText color="var(--text-secondary)" sx={{ marginTop: 'var(--space-1)' }}>
                {Strings.overviewEmpty}
              </AppText>
            </AppCard>
          ) : null}

          {overview.children.map((child: FamilyOverviewChild) => (
            <AppCard key={child.childId}>
              <AppText style="titleS">{child.nickname}</AppText>
              <AppText color="var(--text-secondary)" sx={{ marginTop: 'var(--space-1)' }}>
                {todayLabel(child.todayStatus)}
              </AppText>
              <AppText color="var(--text-secondary)" sx={{ marginTop: 'var(--space-1)' }}>
                {Strings.overviewDays(child.trainingDayCount)}
              </AppText>
              <AppText color="var(--text-secondary)" sx={{ marginTop: 'var(--space-1)' }}>
                {Strings.overviewMinutes(minutesOf(child.totalTrainingTimeMs))}
              </AppText>
              {child.inCalibration ? (
                <AppText color="var(--text-secondary)" sx={{ marginTop: 'var(--space-1)' }}>
                  {Strings.overviewCalibration}
                </AppText>
              ) : null}
              <div style={{ marginTop: 'var(--space-3)' }}>
                <SecondaryButton
                  label={Strings.actionDetails}
                  onClick={() => onOpenDashboard(child.childId)}
                  fullWidth={false}
                />
              </div>
            </AppCard>
          ))}

          {overview.children.length > 0 ? (
            <AppCard>
              <AppText style="titleS">{Strings.overviewTotalsTitle}</AppText>
              <AppText color="var(--text-secondary)" sx={{ marginTop: 'var(--space-1)' }}>
                {Strings.overviewChildCount(overview.totals.childCount)}
              </AppText>
              <AppText color="var(--text-secondary)" sx={{ marginTop: 'var(--space-1)' }}>
                {Strings.overviewDays(overview.totals.trainingDayCount)}
              </AppText>
              <AppText color="var(--text-secondary)" sx={{ marginTop: 'var(--space-1)' }}>
                {Strings.overviewMinutes(minutesOf(overview.totals.totalTrainingTimeMs))}
              </AppText>
            </AppCard>
          ) : null}
        </>
      ) : null}
    </div>
  );
}

/**
 * Milliseconds to whole minutes, rounded down.
 *
 * The API sends milliseconds because that is what it measures; a parent reads minutes. Rounding down rather
 * than to nearest so a first short session never reports a minute that was not spent.
 */
const minutesOf = (milliseconds: number): number => Math.floor(milliseconds / 60_000);

const todayLabel = (status: FamilyOverviewChild['todayStatus']): string => {
  switch (status) {
    case 'in_progress':
      return Strings.overviewTodayInProgress;
    case 'completed':
      return Strings.overviewTodayCompleted;
    default:
      return Strings.overviewTodayNone;
  }
};
